import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useUserStatus } from "@hooks/useUserStatus";
import type {
    ClientsWorkOverviewRow,
    ClientsWorkOverviewWorkRow,
} from "../clientsWorkOverviewViewConfig";
import { buildRolesByUserId } from "@components/organizationView/clients-closes/cells/closeOverviewMemberHelpers";
import { EMPTY_CELL_VALUE } from "./emptyCellValue";

/** The user's roles on the engagement in role order, e.g. "Preparer, Manager"; empty when they have none. */
export const getMyRoles = ({
    row,
    userId,
}: {
    row: ClientsWorkOverviewWorkRow;
    userId: number | undefined;
}) =>
    userId === undefined
        ? ""
        : (buildRolesByUserId(row.preparers, row.reviewers, row.managers)
              .get(userId)
              ?.join(", ") ?? "");

const WorkMyRole = ({ row }: { row: ClientsWorkOverviewWorkRow }) => {
    const { data: userStatus } = useUserStatus();
    return (
        <ViewTableCell>
            {getMyRoles({ row, userId: userStatus?.user.id }) || <EmptyValue />}
        </ViewTableCell>
    );
};

/** The signed-in user's roles on the engagement, or the empty value when they have none. */
export const WorkMyRoleCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, ClientsWorkOverviewRow, unknown>) =>
        row.original.rowType === "work" ? (
            <WorkMyRole row={row.original} />
        ) : (
            EMPTY_CELL_VALUE
        ),
);

WorkMyRoleCell.displayName = "WorkMyRoleCell";
